import { useKeycloak } from '@react-keycloak/web';
import {
  Briefcase,
  Wrench,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  PlugZap,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useMatch } from 'react-router-dom';


import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ARBEITEN_GROUPS, type NavGroup, type NavIcon, type NavItem, VERWALTEN_GROUPS, VERWALTUNG_GROUP } from '@/lib/nav.js';
import { useOrganisations } from '@/lib/organisations.js';
import { useTenant } from '@/lib/tenant.js';
import { cn } from '@/lib/utils';


const GROUP_STORAGE_PREFIX = 'civiport.sidebar.group.';

/**
 * Persistiert per-Group den Offen/Zu-Zustand. Auto-Override: wenn die aktuelle
 * Route in einer Gruppe liegt, ist sie immer als "offen" gerendert (auch wenn
 * der User sie zugeklappt hatte), damit der aktive Eintrag sichtbar bleibt.
 */
function useCollapsibleGroups(
  groupKeys: string[],
  activeGroupKey: string | null,
): {
  isOpen: (key: string) => boolean;
  toggle: (key: string) => void;
} {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    const s = new Set<string>();
    for (const k of groupKeys) {
      if (window.localStorage.getItem(GROUP_STORAGE_PREFIX + k) === '0') s.add(k);
    }
    return s;
  });
  const isOpen = useCallback(
    (key: string) => {
      if (activeGroupKey === key) return true;
      return !collapsed.has(key);
    },
    [collapsed, activeGroupKey],
  );
  const toggle = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      const willBeOpen = prev.has(key);
      if (willBeOpen) next.delete(key);
      else next.add(key);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(GROUP_STORAGE_PREFIX + key, willBeOpen ? '1' : '0');
      }
      return next;
    });
  }, []);
  return { isOpen, toggle };
}


/**
 * Zwei Sichten auf die Navigation:
 *  - "arbeiten":  fuer Mitarbeitende der Kommune — Daten ansehen, Auswertungen
 *                 erstellen, Meldungen verfolgen. Sieht jeder angemeldete Nutzer.
 *  - "verwalten": fuer Systemverwaltung/Datenexperten — Quellen anbinden,
 *                 Datenhaltung, Werkzeuge, System. Nur Realm-Rolle 'admin'.
 * Admins wechseln ueber den Umschalter oben in der Sidebar; Nicht-Admins sehen
 * nur "arbeiten" ohne Umschalter.
 */
export type SidebarView = 'arbeiten' | 'verwalten';

const VIEW_STORAGE_KEY = 'civiport.sidebar.view';

function buildNavGroups(view: SidebarView, canManage: boolean, canWrite: boolean): NavGroup[] {
  if (view === 'verwalten') return VERWALTEN_GROUPS;
  // Redaktion ohne admin: Daten hinzufuegen unter „Erstellen“ (sonst nur in „Verwalten“).
  const groups = canWrite
    ? ARBEITEN_GROUPS.map((g) => (g.key === 'erstellen' ? { ...g, items: [{ to: '/sources/new', label: 'Daten hinzufügen', icon: PlugZap }, ...g.items] } : g))
    : ARBEITEN_GROUPS;
  // Rolle Verwaltung ohne admin: Benutzer ihres Teilbaums unter „Arbeiten“.
  return canManage ? [...groups, VERWALTUNG_GROUP] : groups;
}

/** Liegt der Pfad in einer der Gruppen? (fuer Auto-Umschalten der Sicht) */
function pathInGroups(groups: NavGroup[], path: string): boolean {
  for (const g of groups) {
    for (const it of g.items) {
      if (it.end ? path === it.to : path === it.to || path.startsWith(`${it.to}/`)) return true;
    }
  }
  return false;
}

function readStoredView(): SidebarView {
  if (typeof window === 'undefined') return 'arbeiten';
  return window.localStorage.getItem(VIEW_STORAGE_KEY) === 'verwalten' ? 'verwalten' : 'arbeiten';
}

function readIsAdmin(tokenParsed: unknown): boolean {
  if (!tokenParsed || typeof tokenParsed !== 'object') return false;
  const t = tokenParsed as { realm_access?: { roles?: unknown } };
  const roles = t.realm_access?.roles;
  return Array.isArray(roles) && roles.includes('admin');
}

interface AppSidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Wenn true, wird die Sidebar in einem mobilen Sheet gerendert → Collapse-Toggle und Tooltips entfallen. */
  variant?: 'desktop' | 'mobile';
  /** Callback: Klick auf einen Nav-Link (nur fuer mobile, um Sheet zu schliessen). */
  onNavigate?: () => void;
}

export function AppSidebar({
  collapsed,
  onToggleCollapsed,
  variant = 'desktop',
  onNavigate,
}: AppSidebarProps): JSX.Element {
  const tenant = useTenant();
  const { keycloak } = useKeycloak();
  const isAdmin = readIsAdmin(keycloak.tokenParsed);
  const isMobile = variant === 'mobile';
  const showLabels = isMobile || !collapsed;
  const location = useLocation();
  const [chosenView, setChosenView] = useState<SidebarView>(readStoredView);
  const setView = useCallback((next: SidebarView) => {
    setChosenView(next);
    if (typeof window !== 'undefined') window.localStorage.setItem(VIEW_STORAGE_KEY, next);
  }, []);
  // Die Sicht folgt der Route nur beim Seitenwechsel (Verwalten-Seite aufgerufen
  // -> Verwalten), NICHT bei jedem Rendern — sonst wuerde ein Klick auf den
  // Umschalter auf einer Arbeiten-Seite sofort wieder ueberschrieben.
  useEffect(() => {
    if (!isAdmin) return;
    const path = location.pathname;
    if (pathInGroups(VERWALTEN_GROUPS, path)) setChosenView('verwalten');
    else if (pathInGroups(ARBEITEN_GROUPS, path)) setChosenView('arbeiten');
  }, [isAdmin, location.pathname]);
  // Nicht-Admins sehen immer "arbeiten".
  const view: SidebarView = isAdmin ? chosenView : 'arbeiten';
  const orgs = useOrganisations();
  const canManage = !isAdmin && orgs.items.some((o) => o.can_manage);
  const canWrite = !isAdmin && orgs.my.writable.length > 0;
  const navGroups = useMemo(() => buildNavGroups(view, canManage, canWrite), [view, canManage, canWrite]);
  const activeGroupKey = useMemo(() => {
    const path = location.pathname;
    for (const g of navGroups) {
      if (!g.label) continue; // headerlose Gruppen (Dashboard) sind nicht collapsible
      for (const it of g.items) {
        const match = it.end ? path === it.to : path === it.to || path.startsWith(`${it.to}/`);
        if (match) return g.key;
      }
    }
    return null;
  }, [location.pathname, navGroups]);
  const groupKeys = useMemo(() => navGroups.map((g) => g.key), [navGroups]);
  const { isOpen, toggle } = useCollapsibleGroups(groupKeys, activeGroupKey);

  return (
    <TooltipProvider delayDuration={200}>
      <aside
        aria-label="Hauptnavigation"
        className={cn(
          // Trennlinie zum Inhalt erst unter dem Kopf: oben gehen Logo und Kopfzeile ohne Strich ineinander ueber.
          'relative flex h-full flex-col overflow-x-hidden bg-sidebar text-sidebar-foreground transition-[width] duration-200 after:pointer-events-none after:absolute after:bottom-0 after:right-0 after:top-16 after:w-px after:bg-sidebar-border',
          isMobile ? 'w-full' : collapsed ? 'w-16' : 'w-64',
        )}
      >
        {/* Brand — helles Logo (wenn vorhanden und Platz da ist; sonst Logo auf weissem Feld), sonst Wappen
            oder Monogramm mit Name. Oben die blaue Linie der Kopfzeile, gleich hoch wie diese. */}
        <div
          className={cn(
            'flex h-16 shrink-0 items-center border-b border-t-4 border-b-sidebar-border border-t-brand-500 bg-white px-4',
            !showLabels && 'justify-center',
          )}
        >
          <NavLink
            to="/uebersicht"
            className="flex min-w-0 items-center rounded-md outline-none"
            onClick={onNavigate}
            aria-label={`${tenant.name} — CiviPort Startseite`}
          >
            {showLabels && tenant.logoUrl ? (
              <img src={tenant.logoUrl} alt="" className="h-10 w-auto max-w-[12.5rem] object-contain object-left" />
            ) : showLabels && tenant.logoHellUrl ? (
              // Nur ein Logo fuer dunkle Flaechen: auf einem Feld in der Hausfarbe.
              <span className="flex h-11 items-center rounded-md bg-brand-900 px-2 py-1">
                <img src={tenant.logoHellUrl} alt="" className="h-full w-auto max-w-[12rem] object-contain object-left" />
              </span>
            ) : (
              <>
                {tenant.wappenUrl ? (
                  <img src={tenant.wappenUrl} alt="" className={cn('h-9 w-auto max-w-9 shrink-0 object-contain', showLabels && 'mr-2.5')} />
                ) : (
                  // Ohne Wappen: Monogramm in der Hausfarbe, damit der Kopf nicht nackt wirkt.
                  <span
                    aria-hidden
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-500 font-display text-base font-bold text-white',
                      showLabels && 'mr-2.5',
                    )}
                  >
                    {tenant.shortName.charAt(0).toUpperCase()}
                  </span>
                )}
                {showLabels ? (
                  <div className="flex min-w-0 flex-col leading-tight">
                    <span className="truncate font-display text-sm font-semibold tracking-tight text-foreground">
                      {tenant.name}
                    </span>
                    <span className="text-xs text-muted-foreground">Datenplattform</span>
                  </div>
                ) : null}
              </>
            )}
          </NavLink>
        </div>

        {/* Sicht-Umschalter (nur Admins): Arbeiten | Verwalten */}
        {isAdmin && (
          <div className={cn('px-3 pt-4', !showLabels && 'px-2')}>
            <div
              role="tablist"
              aria-label="Sicht"
              className={cn(
                'flex rounded-lg bg-sidebar-border/60 p-1',
                !showLabels && 'flex-col gap-0.5',
              )}
            >
              <ViewTab
                active={view === 'arbeiten'}
                label="Arbeiten"
                icon={Briefcase}
                collapsed={!showLabels}
                onClick={() => setView('arbeiten')}
              />
              <ViewTab
                active={view === 'verwalten'}
                label="Verwalten"
                icon={Wrench}
                collapsed={!showLabels}
                onClick={() => setView('verwalten')}
              />
            </div>
          </div>
        )}

        {/* Nav */}
        <nav aria-label="Bereiche" className="thin-scroll flex-1 overflow-y-auto overflow-x-hidden pb-4 pt-3">
          {navGroups.map((group, idx) => {
            const items = (
              <ul className="flex flex-col gap-px px-3">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <SidebarLink item={item} collapsed={!showLabels} onNavigate={onNavigate} />
                  </li>
                ))}
              </ul>
            );

            // Collapsed-Modus (nur Icons) oder Gruppe ohne Header (Dashboard):
            // kein Collapsible — Items immer direkt sichtbar.
            if (!showLabels || !group.label) {
              return (
                <div key={group.key} className={cn(idx > 0 && 'mt-3')}>
                  {!showLabels && idx > 0 ? (
                    <Separator className="mx-auto mb-3 w-8 bg-sidebar-border" />
                  ) : null}
                  {items}
                </div>
              );
            }

            const open = isOpen(group.key);
            return (
              <div key={group.key} className={cn(idx > 0 && 'mt-6')}>
                <Collapsible open={open} onOpenChange={() => toggle(group.key)}>
                  <CollapsibleTrigger
                    className="group/header mx-3 flex w-[calc(100%-1.5rem)] items-center justify-between rounded px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground transition-colors hover:text-foreground"
                    aria-label={`${group.label} ${open ? 'einklappen' : 'ausklappen'}`}
                  >
                    <span>{group.label}</span>
                    <ChevronDown
                      className={cn(
                        'h-3.5 w-3.5 opacity-0 transition duration-150 group-hover/header:opacity-100 group-focus-visible/header:opacity-100',
                        !open && '-rotate-90',
                      )}
                      aria-hidden="true"
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                    <div className="pt-1.5">{items}</div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            );
          })}
        </nav>

        {/* Collapse-Toggle (nur Desktop) */}
        {!isMobile && (
          <div className="border-t border-sidebar-border p-3">
            <button
              type="button"
              onClick={onToggleCollapsed}
              className={cn(
                'flex h-9 w-full items-center gap-3 rounded-md px-3 text-[13px] text-muted-foreground outline-none transition-colors hover:bg-black/[0.04] hover:text-foreground',
                collapsed && 'justify-center px-0',
              )}
              aria-label={collapsed ? 'Sidebar ausklappen' : 'Sidebar einklappen'}
              aria-expanded={!collapsed}
            >
              {collapsed ? (
                <PanelLeftOpen className="h-4 w-4" />
              ) : (
                <>
                  <PanelLeftClose className="h-4 w-4" />
                  <span>Einklappen</span>
                </>
              )}
            </button>
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
}

/** Grundform eines Navigationseintrags (36 px hoch, Platz fuer die Markierung links). */
const ITEM =
  'group relative flex h-9 items-center gap-3 rounded-md px-3 text-[13.5px] font-medium outline-none transition-colors';

function SidebarLink({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}): JSX.Element {
  const Icon = item.icon;
  // Aktiv selbst bestimmen (wie NavLink): eingeklappt reicht der Tooltip-Slot die Klassen als Text
  // weiter, eine Klassen-Funktion kaeme dort als Zeichenkette an.
  const isActive = useMatch({ path: item.end ? item.to : `${item.to}/*`, end: true }) !== null;

  if (item.external) {
    const externalLink = (
      <a
        href={item.to}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
        aria-label={collapsed ? item.label : undefined}
        className={cn(
          ITEM,
          collapsed && 'justify-center px-0',
          'text-sidebar-foreground hover:bg-black/[0.04] hover:text-foreground',
        )}
      >
        <Icon className="h-[18px] w-[18px] shrink-0 opacity-60 transition-opacity group-hover:opacity-90" aria-hidden="true" />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </a>
    );
    if (!collapsed) return externalLink;
    return (
      <Tooltip>
        <TooltipTrigger asChild>{externalLink}</TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    );
  }

  const link = (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        ITEM,
        collapsed && 'justify-center px-0',
        isActive
          ? 'bg-sidebar-accent font-semibold text-sidebar-accent-foreground before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-r-full before:bg-sidebar-indicator'
          : 'text-sidebar-foreground hover:bg-black/[0.04] hover:text-foreground',
      )}
      aria-label={collapsed ? item.label : undefined}
    >
      <Icon
        className={cn('h-[18px] w-[18px] shrink-0 transition-opacity', isActive ? 'text-sidebar-indicator' : 'opacity-60 group-hover:opacity-90')}
        aria-hidden="true"
      />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );

  if (!collapsed) {
    return link;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

function ViewTab({
  active,
  label,
  icon: Icon,
  collapsed,
  onClick,
}: {
  active: boolean;
  label: string;
  icon: NavIcon;
  collapsed: boolean;
  onClick: () => void;
}): JSX.Element {
  const button = (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-label={collapsed ? label : undefined}
      onClick={onClick}
      className={cn(
        'flex flex-1 items-center justify-center gap-2 rounded-md px-2 py-1.5 text-xs font-semibold outline-none transition-colors',
        active
          ? 'bg-white text-sidebar-accent-foreground shadow-sm ring-1 ring-black/5'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {!collapsed && <span>{label}</span>}
    </button>
  );
  if (!collapsed) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}
